import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Briefcase, Plus, Pencil, Trash2, Play, Square, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { useTimer } from "@/context/TimerContext";

const statusColor = { active: "bg-[color:var(--kpi-positive)] text-white", paused: "bg-amber-500 text-white", completed: "bg-slate-400 text-white" };
const empty = { client_id: "", title: "", hourly_rate: "", contract_start_date: "", contract_end_date: "", payment_terms: "Net 30 days from invoice date.", status: "active" };

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const { active, clockIn, clockOut } = useTimer();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [j, c] = await Promise.all([api.get("/jobs"), api.get("/clients")]);
      setJobs(j.data); setClients(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (j) => { setEditing(j); setForm({ client_id: j.client_id, title: j.title, hourly_rate: j.hourly_rate, contract_start_date: j.contract_start_date || "", contract_end_date: j.contract_end_date || "", payment_terms: j.payment_terms || "", status: j.status }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Please select a client");
    const payload = { ...form, hourly_rate: parseFloat(form.hourly_rate) || 0 };
    try {
      if (editing) { await api.put(`/jobs/${editing.id}`, payload); toast.success("Job updated"); }
      else { await api.post("/jobs", payload); toast.success("Job created"); }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
  };
  const remove = async (j) => {
    try { await api.delete(`/jobs/${j.id}`); toast.success("Job deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div>
      <PageHeader title="Jobs" subtitle="Engagements, rates and contract terms per client."
        actions={<Button data-testid="jobs-create-button" onClick={openNew}><Plus className="h-4 w-4 mr-2" />New job</Button>} />

      {loading ? <Card className="p-8 text-center text-muted-foreground">Loading...</Card>
      : jobs.length === 0 ? (
        <EmptyState icon={Briefcase} title="No jobs yet" description={clients.length === 0 ? "Add a client first, then create a job." : "Create a job to track hours and bill your client."}
          action={clients.length === 0 ? <Button variant="outline" onClick={() => navigate("/clients")}>Go to clients</Button> : <Button data-testid="jobs-empty-create-button" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Create job</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <Table data-testid="jobs-table">
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead className="hidden md:table-cell">Client</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Timer / Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((j) => {
                const running = active && active.job_id === j.id;
                return (
                  <TableRow key={j.id} data-testid={`job-row-${j.id}`} className="hover:bg-accent/50">
                    <TableCell>
                      <div className="font-medium">{j.title}</div>
                      <div className="text-xs text-muted-foreground md:hidden">{j.client_name}</div>
                      {(j.contract_start_date || j.contract_end_date) && <div className="text-xs text-muted-foreground">{fmtDate(j.contract_start_date)} – {fmtDate(j.contract_end_date)}</div>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{j.client_name}</TableCell>
                    <TableCell className="text-right tabular-nums font-mono">{money(j.hourly_rate)}/hr</TableCell>
                    <TableCell><Badge className={`capitalize ${statusColor[j.status]}`} data-testid={`job-status-badge-${j.id}`}>{j.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {running ? (
                          <Button size="sm" variant="destructive" data-testid={`job-clock-out-button-${j.id}`} onClick={clockOut}><Square className="h-3.5 w-3.5 mr-1" />Stop</Button>
                        ) : (
                          <Button size="sm" variant="outline" data-testid={`job-clock-in-button-${j.id}`} disabled={!!active} onClick={() => clockIn(j.id)}><Play className="h-3.5 w-3.5 mr-1" />Clock in</Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Agreement" data-testid={`job-agreement-${j.id}`} onClick={() => navigate(`/agreements?job=${j.id}`)}><FileSignature className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`job-edit-${j.id}`} onClick={() => openEdit(j)}><Pencil className="h-4 w-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`job-delete-${j.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete {j.title}?</AlertDialogTitle><AlertDialogDescription>This deletes the job, its agreement and timesheet entries.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={() => remove(j)}>Delete</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>{editing ? "Edit job" : "New job"}</SheetTitle></SheetHeader>
          <form onSubmit={save} className="space-y-4 mt-6">
            <div className="space-y-1.5"><Label>Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="job-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Job title *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title-input" placeholder="Website Redesign 2025" /></div>
            <div className="space-y-1.5"><Label>Hourly rate ($) *</Label><Input required type="number" step="0.01" min="0" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="job-rate-input" placeholder="85" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Start date</Label><DatePicker value={form.contract_start_date} onChange={(v) => setForm({ ...form, contract_start_date: v })} testid="job-start-date" /></div>
              <div className="space-y-1.5"><Label>End date</Label><DatePicker value={form.contract_end_date} onChange={(v) => setForm({ ...form, contract_end_date: v })} testid="job-end-date" /></div>
            </div>
            <div className="space-y-1.5"><Label>Payment terms</Label><Textarea value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} data-testid="job-payment-terms-input" /></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="job-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
              </Select>
            </div>
            <SheetFooter className="pt-2"><Button type="submit" className="w-full" data-testid="job-save-button">{editing ? "Save changes" : "Create job"}</Button></SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
